/**
 * Store for managing the currently selected project and its details.
 * Handles fetching project details, messages, and project-specific actions.
 */
import { create } from 'zustand';
import { HttpAgentAdapter } from '../services/agent/HttpAgentAdapter';
import type {
  BusinessCaseDetails,
  AgentUpdate,
  InitiateCasePayload,
  InitiateCaseResponse,
  ProvideFeedbackPayload,
} from '../services/agent/AgentService';
import { toAppError, AppError } from '../types/api';
import Logger from '../utils/logger';

const logger = Logger.create('currentProjectStore');

// Singleton service instance
const agentService = new HttpAgentAdapter();

interface CurrentProjectState {
  // Data
  projectId: string | null;
  projectDetails: BusinessCaseDetails | null;
  messages: AgentUpdate[];

  // Loading states
  isLoading: boolean;
  isLoadingDetails: boolean;
  isSendingMessage: boolean;

  // Errors
  error: AppError | null;
  detailsError: AppError | null;

  // Actions
  fetchDetails: (projectId: string) => Promise<void>;
  refreshDetails: () => Promise<void>;
  initiateProject: (
    payload: InitiateCasePayload
  ) => Promise<InitiateCaseResponse | undefined>;
  sendMessage: (payload: ProvideFeedbackPayload) => Promise<void>;
  clearCurrentProject: () => void;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  projectId: null,
  projectDetails: null,
  messages: [],
  isLoading: false,
  isLoadingDetails: false,
  isSendingMessage: false,
  error: null,
  detailsError: null,
};

export const useCurrentProjectStore = create<CurrentProjectState>(
  (set, get) => ({
    ...initialState,

    fetchDetails: async (projectId: string) => {
      set({
        isLoadingDetails: true,
        detailsError: null,
        projectDetails: null,
      });

      try {
        logger.debug(`Fetching project details: ${projectId}`);
        const details = await agentService.getCaseDetails(projectId);

        set({
          projectId,
          projectDetails: details,
          messages: details.history || [],
          isLoadingDetails: false,
        });

        logger.debug(`Project details loaded for: ${projectId}`);
      } catch (err) {
        const appError = toAppError(err, 'api');
        logger.error('Error fetching project details:', appError);
        set({ isLoadingDetails: false, detailsError: appError });
      }
    },

    refreshDetails: async () => {
      const { projectId } = get();
      if (!projectId) {
        logger.debug('refreshDetails: No current project, skipping');
        return;
      }

      try {
        logger.debug(`Refreshing project details: ${projectId}`);
        const details = await agentService.getCaseDetails(projectId);

        set({
          projectDetails: details,
          messages: details.history || [],
        });
      } catch (err) {
        logger.warn('Failed to refresh project details:', err);
      }
    },

    initiateProject: async (payload: InitiateCasePayload) => {
      set({ isLoading: true, error: null });

      try {
        logger.debug('Initiating new project');
        const response = await agentService.initiateCase(payload);

        set({
          isLoading: false,
          projectId: response.caseId,
          messages: response.initialMessage
            ? [
                {
                  caseId: response.caseId,
                  timestamp: new Date().toISOString(),
                  source: 'AGENT',
                  messageType: 'TEXT',
                  content: response.initialMessage,
                },
              ]
            : [],
        });

        logger.debug(`Project initiated: ${response.caseId}`);
        return response;
      } catch (err) {
        const appError = toAppError(err, 'api');
        logger.error('Error initiating project:', appError);
        set({ isLoading: false, error: appError });
        return undefined;
      }
    },

    sendMessage: async (payload: ProvideFeedbackPayload) => {
      set({ isSendingMessage: true, error: null });

      // Optimistically add user message
      const userMessage: AgentUpdate = {
        caseId: payload.caseId,
        timestamp: new Date().toISOString(),
        source: 'USER',
        messageType: 'TEXT',
        content: payload.message,
      };

      set((state) => ({
        messages: [...state.messages, userMessage],
      }));

      try {
        await agentService.provideFeedback(payload);
        set({ isSendingMessage: false });

        // Refresh to get agent response
        await get().refreshDetails();
      } catch (err) {
        const appError = toAppError(err, 'api');
        logger.error('Error sending message:', appError);
        set({ isSendingMessage: false, error: appError });
      }
    },

    clearCurrentProject: () => {
      logger.debug('Clearing current project');
      set({
        projectId: null,
        projectDetails: null,
        messages: [],
        detailsError: null,
      });
    },

    clearError: () => set({ error: null, detailsError: null }),

    reset: () => set(initialState),
  })
);

// Selector hooks for optimized subscriptions
export const useCurrentProjectId = () =>
  useCurrentProjectStore((state) => state.projectId);
export const useCurrentProjectDetails = () =>
  useCurrentProjectStore((state) => state.projectDetails);
export const useProjectMessages = () =>
  useCurrentProjectStore((state) => state.messages);
export const useCurrentProjectLoading = () =>
  useCurrentProjectStore((state) => state.isLoadingDetails);
export const useCurrentProjectError = () =>
  useCurrentProjectStore((state) => state.detailsError);
